'use client'

import { useTranslations } from 'next-intl'
import Text from '@shared/components/Text/Text'
import type { DocumentSearchResponse, IDocument } from '@shared/lib/search.server'
import { LoggedErrorBoundary } from '@shared/components/ErrorBoundary/LoggedErrorBoundary'
import type { IRichResult } from './SearchProvider'

type GroupedHit = NonNullable<DocumentSearchResponse['grouped_hits']>[number]
type SearchDocument = Partial<IDocument> & Record<string, unknown>

interface SearchResultBoundaryProps {
  children: React.ReactNode
  context:
    | {
        kind: 'grouped-hit'
        location: string
        query?: string
        result: GroupedHit
      }
    | {
        kind: 'rich-result'
        location: string
        query?: string
        result: IRichResult
      }
}

export function SearchResultBoundary({
  children,
  context,
}: SearchResultBoundaryProps) {
  const t = useTranslations('SearchResults')

  return (
    <LoggedErrorBoundary
      fallbackRender={() => (
        <Text align='center' style={{ maxWidth: 'unset' }} color='tertiary'>
          {t('errorMessage')}
        </Text>
      )}
      logLabel='[SearchResultBoundary] Failed to render search result'
      // Search payloads come from external indexing data; log a compact summary so malformed records are debuggable without crashing the whole list.
      logContext={() => serializeContext(context)}
    >
      {children}
    </LoggedErrorBoundary>
  )
}

function serializeContext(context: SearchResultBoundaryProps['context']) {
  if (context.kind === 'grouped-hit') {
    return {
      kind: context.kind,
      location: context.location,
      query: context.query,
      groupKey: context.result.group_key,
      hitsCount: context.result.hits?.length ?? 0,
      firstHit: summarizeDocument(
        context.result.hits?.[0]?.document as SearchDocument | undefined,
      ),
    }
  }

  return {
    kind: context.kind,
    location: context.location,
    query: context.query,
    result: summarizeDocument(context.result as SearchDocument),
  }
}

function summarizeDocument(document?: SearchDocument) {
  if (!document) return undefined

  return removeUndefined({
    id: getString(document.id),
    normId: getString(document.normId),
    documentType: getString(document.documentType),
    title: getString(document.title),
    abbreviation: getString(document.abbreviation),
    caseNumber: getString(
      document.caseNumber ?? document.Geschaeftszahl ?? document.Rechtssatznummer,
    ),
    lawType: getString(document.lawType),
    institution: getStringOrFirst(document.institution),
    Applikation: getString(document.Applikation),
    Gericht: getString(document.Gericht),
    Organ: getString(document.Organ),
    Typ: getString(document.Typ),
  })
}

function getString(value: unknown) {
  return typeof value === 'string' ? value : undefined
}

function getStringOrFirst(value: unknown) {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return getString(value[0])
  return undefined
}

function removeUndefined<T extends Record<string, unknown>>(value: T) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  )
}
